import { FileDown, FileUp, Info, Plus, Search } from 'lucide-react'
import { AnimatePresence } from 'motion/react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { TableMasterResponse } from '@/lib/api/client/types.gen'
import { downloadTablesCSV } from '@/lib/csv'

import { AssignGuestDialog } from './AssignGuestDialog'
import { ImportTablesDialog } from './ImportTablesDialog'
import { MasterTable } from './MasterTable'
import type { TableFormValues } from './TableFormDialog'
import { TableFormDialog } from './TableFormDialog'
import { TableItem } from './TableItem'
import { TableLayoutEditor } from './TableLayoutEditor'
import { useTableList } from './useTableList'

export const TablesPageMobile = () => {
  const {
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    tableList,
    isLoading,
    isFetchingNextPage,
    totalCount,
    loadMoreRef,
    handleRefresh,
    createMutation,
    updateMutation,
    deleteMutation,
    importMutation,
    tableOccupancyMap,
    tables,
  } = useTableList()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [isAssignOpen, setIsAssignOpen] = useState(false)
  const [editingTable, setEditingTable] = useState<TableMasterResponse | null>(
    null,
  )
  const [targetTable, setTargetTable] = useState<TableMasterResponse | null>(
    null,
  )

  const handleEdit = (table: TableMasterResponse) => {
    setEditingTable(table)
    setIsFormOpen(true)
  }

  const handleAddGuest = (table: TableMasterResponse) => {
    setTargetTable(table)
    setIsAssignOpen(true)
  }

  const handleDelete = (id: string) => {
    if (confirm('คุณแน่ใจหรือไม่ว่าต้องการลบโต๊ะนี้?')) {
      deleteMutation.mutate({ path: { id } })
    }
  }

  const handleFormSubmit = async (values: TableFormValues) => {
    if (editingTable) {
      await updateMutation.mutateAsync({
        path: { id: editingTable.id },
        body: values,
      })
    } else {
      await createMutation.mutateAsync({
        body: values,
      })
    }
  }

  const handleImport = async (file: File) => {
    await importMutation.mutateAsync({
      body: { file },
    })
  }

  const handleExport = () => {
    const sorted = [...tables].sort((a, b) => {
      let comparison = 0
      const sortCol = sortBy || 'tableNumber'
      const isAsc = sortOrder !== 'desc'

      if (sortCol === 'tableNumber') {
        const valA = String(a.tableNumber)
        const valB = String(b.tableNumber)
        const numA = Number(valA)
        const numB = Number(valB)
        const aIsNum = !isNaN(numA) && valA.trim() !== ''
        const bIsNum = !isNaN(numB) && valB.trim() !== ''

        if (aIsNum && bIsNum) {
          comparison = numA - numB
        } else if (aIsNum) {
          comparison = -1
        } else if (bIsNum) {
          comparison = 1
        } else {
          comparison = valA.localeCompare(valB, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        }
      } else if (sortCol === 'capacity') {
        comparison = (a.capacity || 0) - (b.capacity || 0)
      } else if (sortCol === 'createdAt') {
        const getTime = (val: any) => {
          if (!val) return 0
          if (typeof val.toDate === 'function') return val.toDate().getTime()
          if (val.seconds) return val.seconds * 1000
          return new Date(val).getTime()
        }
        const timeA = getTime(a.createdAt)
        const timeB = getTime(b.createdAt)
        comparison = timeA - timeB
      }
      return isAsc ? comparison : -comparison
    })

    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[^0-9]/g, '')
    downloadTablesCSV(sorted, `tables_export_${timestamp}.csv`)
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-white">
      <header className="fixed inset-x-0 top-0 z-30 flex flex-col gap-4 border-b bg-white/80 px-4 pt-4 pb-4 backdrop-blur-md">
        <div className="flex items-center justify-between">
          <h1 className="text-foreground text-2xl font-bold">จัดการโต๊ะ</h1>
          <div className="flex gap-2">
            <Button
              size="icon"
              variant="outline"
              className="text-primary border-primary/20 bg-primary/5 hover:bg-primary/10 size-9 rounded-full font-semibold"
              onClick={handleExport}
            >
              <FileDown className="size-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="text-primary border-primary/20 bg-primary/5 hover:bg-primary/10 size-9 rounded-full font-semibold"
              onClick={() => setIsImportOpen(true)}
            >
              <FileUp className="size-4" />
            </Button>
            <Button
              size="sm"
              variant="default"
              className="h-9 rounded-full px-4 font-semibold"
              onClick={() => {
                setEditingTable(null)
                setIsFormOpen(true)
              }}
            >
              <Plus className="mr-1.5 size-4" />
              เพิ่มโต๊ะ
            </Button>
          </div>
        </div>

        <div className="text-muted-foreground -mt-1 text-xs font-medium">
          จำนวนโต๊ะทั้งหมด{' '}
          <span className="text-foreground font-bold">{totalCount}</span>
        </div>

        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder="ค้นหาเลขโต๊ะหรือโซน..."
            className="bg-muted/30 focus-visible:ring-primary/20 h-11 rounded-xl border-none pl-10 shadow-none focus-visible:ring-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex gap-2 pb-1">
          <NativeSelect
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [by, order] = e.target.value.split('-')
              setSortBy(by)
              setSortOrder(order)
            }}
            className="shrink-0"
            selectClassName="h-9 bg-slate-50 border border-slate-200 rounded-full text-xs pl-3 pr-8 focus-visible:ring-1 focus-visible:ring-primary/20 font-medium cursor-pointer transition-colors"
          >
            <NativeSelectOption value="tableNumber-asc">
              เรียงตาม: เลขโต๊ะ (น้อย-มาก)
            </NativeSelectOption>
            <NativeSelectOption value="tableNumber-desc">
              เรียงตาม: เลขโต๊ะ (มาก-น้อย)
            </NativeSelectOption>
            <NativeSelectOption value="capacity-asc">
              เรียงตาม: ความจุ (น้อย-มาก)
            </NativeSelectOption>
            <NativeSelectOption value="capacity-desc">
              เรียงตาม: ความจุ (มาก-น้อย)
            </NativeSelectOption>
            <NativeSelectOption value="createdAt-desc">
              เรียงตาม: ใหม่สุด
            </NativeSelectOption>
            <NativeSelectOption value="createdAt-asc">
              เรียงตาม: เก่าสุด
            </NativeSelectOption>
          </NativeSelect>
        </div>
      </header>

      <Tabs
        defaultValue="list"
        className="flex flex-1 flex-col overflow-hidden"
      >
        <div className="bg-white/80 px-4 pt-52 pb-2 backdrop-blur-md">
          <TabsList className="w-full rounded-xl border bg-white/50 p-1 backdrop-blur-sm">
            <TabsTrigger
              value="list"
              className="flex-1 rounded-lg py-2 text-sm font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              รายการโต๊ะ
            </TabsTrigger>
            <TabsTrigger
              value="layout"
              className="flex-1 rounded-lg py-2 text-sm font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              แผนผังที่นั่ง
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="list"
          className="m-0 flex-1 overflow-y-auto"
        >
          <main className="px-4 pb-6">
            <AnimatePresence mode="wait">
              {isLoading ? (
                <div className="flex flex-col gap-4">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-muted h-32 w-full animate-pulse rounded-2xl"
                    />
                  ))}
                </div>
              ) : tableList.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {tableList.map((table) => (
                    <TableItem
                      key={table.id}
                      id={table.id}
                      tableNumber={table.tableNumber}
                      capacity={table.capacity}
                      occupancy={tableOccupancyMap[table.id] || 0}
                      notes={table.notes}
                      type={table.type}
                      onEdit={() => handleEdit(table)}
                      onDelete={() => handleDelete(table.id)}
                      onAddGuest={() => handleAddGuest(table)}
                    />
                  ))}

                  <div
                    ref={loadMoreRef}
                    className="flex justify-center py-1"
                  >
                    {isFetchingNextPage && (
                      <div className="border-primary size-6 animate-spin rounded-full border-2 border-t-transparent" />
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="bg-muted mb-4 flex size-16 items-center justify-center rounded-full">
                    <Search className="text-muted-foreground size-8" />
                  </div>
                  <h3 className="text-lg font-bold">ไม่พบข้อมูลโต๊ะ</h3>
                  <p className="text-muted-foreground mt-1">
                    {debouncedQuery
                      ? `ไม่พบโต๊ะที่ตรงกับ "${debouncedQuery}"`
                      : 'ยังไม่มีการเพิ่มโต๊ะในระบบ'}
                  </p>
                </div>
              )}
            </AnimatePresence>
          </main>
        </TabsContent>

        <TabsContent
          value="layout"
          className="m-0 flex-1 p-4"
        >
          <div className="flex h-full flex-col items-center justify-center rounded-3xl border-2 border-dashed bg-gray-50/50 p-8 text-center">
            <div className="bg-primary/10 text-primary mb-4 flex size-16 items-center justify-center rounded-full">
              <Info className="size-8" />
            </div>
            <h3 className="text-lg font-bold">โหมดแผนผังที่นั่ง</h3>
            <p className="text-muted-foreground mt-2 max-w-xs text-sm">
              การจัดการแผนผังที่นั่งเหมาะสำหรับใช้งานบนหน้าจอขนาดใหญ่ (Desktop)
              เพื่อความแม่นยำในการจัดวาง
            </p>
          </div>
        </TabsContent>
      </Tabs>

      <TableFormDialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        initialValues={editingTable || undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      <ImportTablesDialog
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImport={handleImport}
        isPending={importMutation.isPending}
      />

      <AssignGuestDialog
        isOpen={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
        tableId={targetTable?.id || ''}
        tableNumber={targetTable?.tableNumber || ''}
      />
    </div>
  )
}

export const TablesPageDesktop = () => {
  const {
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    tableList,
    isLoading,
    isFetchingNextPage,
    totalCount,
    loadMoreRef,
    createMutation,
    updateMutation,
    deleteMutation,
    importMutation,
    tableOccupancyMap,
    tables,
  } = useTableList()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [isAssignOpen, setIsAssignOpen] = useState(false)
  const [editingTable, setEditingTable] = useState<TableMasterResponse | null>(
    null,
  )
  const [targetTable, setTargetTable] = useState<TableMasterResponse | null>(
    null,
  )

  const handleEdit = (table: TableMasterResponse) => {
    setEditingTable(table)
    setIsFormOpen(true)
  }

  const handleAddGuest = (table: TableMasterResponse) => {
    setTargetTable(table)
    setIsAssignOpen(true)
  }

  const handleDelete = (id: string) => {
    if (confirm('คุณแน่ใจหรือไม่ว่าต้องการลบโต๊ะนี้?')) {
      deleteMutation.mutate({ path: { id } })
    }
  }

  const handleFormSubmit = async (values: TableFormValues) => {
    if (editingTable) {
      await updateMutation.mutateAsync({
        path: { id: editingTable.id },
        body: values,
      })
    } else {
      await createMutation.mutateAsync({
        body: values,
      })
    }
  }

  const handleImport = async (file: File) => {
    await importMutation.mutateAsync({
      body: { file },
    })
  }

  const handleExport = () => {
    const sorted = [...tables].sort((a, b) => {
      let comparison = 0
      const sortCol = sortBy || 'tableNumber'
      const isAsc = sortOrder !== 'desc'

      if (sortCol === 'tableNumber') {
        const valA = String(a.tableNumber)
        const valB = String(b.tableNumber)
        const numA = Number(valA)
        const numB = Number(valB)
        const aIsNum = !isNaN(numA) && valA.trim() !== ''
        const bIsNum = !isNaN(numB) && valB.trim() !== ''

        if (aIsNum && bIsNum) {
          comparison = numA - numB
        } else if (aIsNum) {
          comparison = -1
        } else if (bIsNum) {
          comparison = 1
        } else {
          comparison = valA.localeCompare(valB, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        }
      } else if (sortCol === 'capacity') {
        comparison = (a.capacity || 0) - (b.capacity || 0)
      } else if (sortCol === 'createdAt') {
        const getTime = (val: any) => {
          if (!val) return 0
          if (typeof val.toDate === 'function') return val.toDate().getTime()
          if (val.seconds) return val.seconds * 1000
          return new Date(val).getTime()
        }
        const timeA = getTime(a.createdAt)
        const timeB = getTime(b.createdAt)
        comparison = timeA - timeB
      }
      return isAsc ? comparison : -comparison
    })

    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[^0-9]/g, '')
    downloadTablesCSV(sorted, `tables_export_${timestamp}.csv`)
  }

  const tableData = useMemo(() => {
    return tableList.map((table) => ({
      ...table,
      occupancy: tableOccupancyMap[table.id] || 0,
    }))
  }, [tableList, tableOccupancyMap])

  const handleUpdatePosition = async (
    id: string,
    x: number,
    y: number,
    extra?: Partial<TableMasterResponse>,
  ) => {
    await updateMutation.mutateAsync({
      path: { id },
      body: { x, y, ...extra },
    })
  }

  return (
    <div className="bg-background-light flex flex-1 flex-col overflow-hidden">
      <header className="bg-white px-8 pt-6 pb-6 shadow-xs">
        <div className="mx-auto flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                จัดการโต๊ะ
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                จัดการข้อมูลโต๊ะ โซน และจำนวนที่นั่งทั้งหมด
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="text-primary border-primary/20 bg-primary/5 hover:bg-primary/10 h-12 rounded-xl px-6 font-bold transition-all active:scale-95"
                onClick={handleExport}
              >
                <FileDown className="mr-2 size-5" />
                ส่งออก CSV
              </Button>
              <Button
                variant="outline"
                className="text-primary border-primary/20 bg-primary/5 hover:bg-primary/10 h-12 rounded-xl px-6 font-bold transition-all active:scale-95"
                onClick={() => setIsImportOpen(true)}
              >
                <FileUp className="mr-2 size-5" />
                นำเข้า CSV
              </Button>
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 shadow-primary/20 h-12 rounded-xl px-6 font-bold shadow-lg transition-all active:scale-95"
                onClick={() => {
                  setEditingTable(null)
                  setIsFormOpen(true)
                }}
              >
                <Plus className="mr-2 size-5" />
                เพิ่มโต๊ะใหม่
              </Button>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between">
            <div className="bg-primary/10 text-primary flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold">
              <div className="size-2 animate-pulse rounded-full bg-current" />
              โต๊ะทั้งหมด {totalCount} รายการ
            </div>

            <div className="flex items-center gap-3">
              <div className="relative w-full md:w-80">
                <Search className="text-muted-foreground absolute top-1/2 left-4 size-4 -translate-y-1/2" />
                <Input
                  placeholder="ค้นหาเลขโต๊ะหรือโซน..."
                  className="bg-muted/30 focus-visible:ring-primary/20 h-12 rounded-xl border-none pl-12 text-base shadow-none transition-all focus-visible:bg-white focus-visible:ring-2"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <NativeSelect
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [by, order] = e.target.value.split('-')
                  setSortBy(by)
                  setSortOrder(order)
                }}
                className="w-full min-w-42.5 sm:w-auto"
                selectClassName="h-12 bg-slate-50 hover:bg-slate-100/80 border border-slate-200 rounded-xl text-sm pl-4 pr-10 focus-visible:ring-2 focus-visible:ring-primary/20 font-medium w-full cursor-pointer transition-colors"
              >
                <NativeSelectOption value="tableNumber-asc">
                  เรียงตาม: เลขโต๊ะ (น้อย-มาก)
                </NativeSelectOption>
                <NativeSelectOption value="tableNumber-desc">
                  เรียงตาม: เลขโต๊ะ (มาก-น้อย)
                </NativeSelectOption>
                <NativeSelectOption value="capacity-asc">
                  เรียงตาม: ความจุ (น้อย-มาก)
                </NativeSelectOption>
                <NativeSelectOption value="capacity-desc">
                  เรียงตาม: ความจุ (มาก-น้อย)
                </NativeSelectOption>
                <NativeSelectOption value="createdAt-desc">
                  เรียงตาม: ใหม่สุด
                </NativeSelectOption>
                <NativeSelectOption value="createdAt-asc">
                  เรียงตาม: เก่าสุด
                </NativeSelectOption>
              </NativeSelect>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-hidden px-8 pt-6 pb-10">
        <div className="mx-auto flex h-full max-w-7xl flex-col">
          <Tabs
            defaultValue="list"
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="mb-6 flex items-center justify-between">
              <TabsList className="w-80 rounded-xl border bg-white/50 p-1 backdrop-blur-sm">
                <TabsTrigger
                  value="list"
                  className="flex-1 rounded-lg py-2 text-sm font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  รายการโต๊ะ
                </TabsTrigger>
                <TabsTrigger
                  value="layout"
                  className="flex-1 rounded-lg py-2 text-sm font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  แผนผังที่นั่ง
                </TabsTrigger>
              </TabsList>

              <div
                className={`flex items-center gap-2 text-sm text-gray-400 transition-opacity ${updateMutation.isPending ? 'opacity-100' : 'opacity-0'}`}
              >
                <div className="border-primary size-4 animate-spin rounded-full border-2 border-t-transparent" />
                กำลังอัปเดต...
              </div>
            </div>

            <TabsContent
              value="list"
              className="m-0 flex-1 overflow-y-auto pr-2"
            >
              <AnimatePresence mode="wait">
                {isLoading ? (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className="h-48 w-full animate-pulse rounded-2xl border bg-white/50"
                      />
                    ))}
                  </div>
                ) : tableList.length > 0 ? (
                  <div className="flex flex-col gap-8">
                    <MasterTable
                      data={tableData}
                      isLoading={isLoading}
                      totalCount={totalCount}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onAddGuest={handleAddGuest}
                    />
                  </div>
                ) : (
                  <div className="flex h-96 flex-col items-center justify-center text-center">
                    <div className="mb-6 flex size-20 items-center justify-center rounded-3xl bg-white shadow-sm">
                      <Search className="text-muted-foreground size-10" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      ไม่พบข้อมูลโต๊ะ
                    </h3>
                    <p className="text-muted-foreground mt-2 max-w-xs text-lg">
                      {debouncedQuery
                        ? `ไม่พบโต๊ะที่ตรงกับ "${debouncedQuery}"`
                        : 'ยังไม่มีการเพิ่มโต๊ะในระบบ เริ่มต้นโดยการเพิ่มโต๊ะใหม่'}
                    </p>
                    {!debouncedQuery && (
                      <Button
                        className="mt-8 rounded-full px-8"
                        onClick={() => setIsFormOpen(true)}
                      >
                        เพิ่มโต๊ะแรกของคุณ
                      </Button>
                    )}
                  </div>
                )}
              </AnimatePresence>
            </TabsContent>

            <TabsContent
              value="layout"
              className="m-0 flex-1 overflow-hidden"
            >
              <TableLayoutEditor
                tables={tableList}
                tableOccupancyMap={tableOccupancyMap}
                onUpdatePosition={handleUpdatePosition}
                isLoading={isLoading}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <TableFormDialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        initialValues={editingTable || undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      <ImportTablesDialog
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImport={handleImport}
        isPending={importMutation.isPending}
      />

      <AssignGuestDialog
        isOpen={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
        tableId={targetTable?.id || ''}
        tableNumber={targetTable?.tableNumber || ''}
      />
    </div>
  )
}
